import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import EditControls from './table/edit_controls';
import { getStandardAttributes } from '../helpers/attributes';
import NormalControls from './table/normal_controls';
import { addBlurHandler, parseTableNode, removeBlurHandler } from '../helpers/table_dom_helper';
import TableBody from './table/body';
import TableHeader from './table/header';
import TableOverrideHelper, { overrideShape } from '../helpers/table_override_helper';

class Table extends React.Component {
  static propTypes = {
    allowColumnEditing: PropTypes.bool.isRequired,
    allowRowEditing: PropTypes.bool.isRequired,
    defaultNode: PropTypes.object.isRequired,
    highlight: PropTypes.bool,
    newColumnClass: PropTypes.string,
    newColumnStyle: PropTypes.string,
    newRowClass: PropTypes.string,
    newRowStyle: PropTypes.string,
    override: overrideShape,
    sanitize: PropTypes.bool,

    onOverrideChange: PropTypes.func.isRequired,
    onOverrideReset: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props);

    const table = props.defaultNode;

    this.id = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString();

    // This is the target document the render target's document (the IFrame's document),
    // whereas `document` is the outer document - we need both for focus/blur handling
    this.doc = table.ownerDocument;

    this.attributes = getStandardAttributes(table),
    this.defaults = parseTableNode(table);

    this.overrideHelper = new TableOverrideHelper(this.defaults, {
      newColumn: {
        class: this.props.newColumnClass,
        style: this.props.newColumnStyle,
      },
      newRow: {
        class: this.props.newRowClass,
        style: this.props.newRowStyle
      }
    });

    this.state = {
      editing: false,
      focused: false,
      hovering: false
    };
  }

  componentWillUnmount() {
    removeBlurHandler(this);
  }

  handleBlur = () => {
    this.setState(({ selectedSection }) => {
      if (selectedSection == null) {
        removeBlurHandler(this);
        return { editing: false };
      } else {
        return {
          selectedColumnIndex: null,
          selectedRowIndex: null,
          selectedSection: null
        };
      }
    });
  }

  handleCellBlur = () => {
    this.setState({ focused: false });
  }

  handleCellFocus = () => {
    this.setState({ focused: true });
  }

  startEditing = (e) => {
    e.stopPropagation();
    this.setState({ editing: true });
    addBlurHandler(this, this.id, this.doc, this.handleBlur);
  }

  stopEditing = (callback) => {
    this.setState({
      editing: false,
      selectedRowIndex: null,
      selectedColumnIndex: null,
      selectedSection: null
    }, () => {
      if (callback instanceof Function) {
        callback();
      }
    });

    removeBlurHandler(this);
  }

  handleMouseEnter = () => {
    this.setState({ hovering: true });
  }

  handleMouseLeave = () => {
    this.setState({ hovering: false });
  }

  handleCellOverrideChange = (html, { columnIndex, rowIndex, section }) => {
    const override = this.overrideHelper.changeCellAt(section, rowIndex, columnIndex, html, this.props.override);
    this.props.onOverrideChange(override);
  }

  handleCellOverrideReset = ({ columnIndex, rowIndex, section }) => {
    const override = this.overrideHelper.resetCellAt(section, rowIndex, columnIndex, this.props.override);
    this.props.onOverrideChange(override);
  }

  handleCellSelect = ({ columnIndex, rowIndex, section }) => {
    this.setState({
      selectedColumnIndex: columnIndex,
      selectedRowIndex: rowIndex,
      selectedSection: section
    });
  }

  addColumnAfter = () => {
    const override = this.overrideHelper.addColumnAfter(this.state.selectedColumnIndex, this.props.override);
    this.props.onOverrideChange(override);
  }

  addColumnBefore = () => {
    this.setState(({ selectedColumnIndex: index }) => {
      const override = this.overrideHelper.addColumnBefore(index, this.props.override);
      this.props.onOverrideChange(override);

      if (index != null) {
        return { selectedColumnIndex: index + 1 };
      }
    });
  }

  deleteColumn = () => {
    this.setState(({ selectedColumnIndex: index }) => {
      const override = this.overrideHelper.deleteColumnAt(index, this.props.override);
      this.props.onOverrideChange(override);

      if (index >= this.overrideHelper.getColumnCount(override)) {
        return { selectedColumnIndex: index - 1 };
      }
    });
  }

  moveColumnLeft = () => {
    this.setState(({ selectedColumnIndex: index }) => {
      const override = this.overrideHelper.swapColumns(index, index - 1, this.props.override);
      this.props.onOverrideChange(override);
      return { selectedColumnIndex: index - 1 };
    });
  }

  moveColumnRight = () => {
    this.setState(({ selectedColumnIndex: index }) => {
      const override = this.overrideHelper.swapColumns(index, index + 1, this.props.override);
      this.props.onOverrideChange(override);
      return { selectedColumnIndex: index + 1 };
    });
  }

  addRowAfter = () => {
    const override = this.overrideHelper.addRowAfter(this.state.selectedRowIndex, this.props.override);
    this.props.onOverrideChange(override);
  }

  addRowBefore = () => {
    this.setState(({ selectedRowIndex: index }) => {
      const override = this.overrideHelper.addRowBefore(index, this.props.override);
      this.props.onOverrideChange(override);

      if (index != null) {
        return { selectedRowIndex: index + 1 };
      }
    });
  }

  deleteRow = () => {
    this.setState(({ selectedRowIndex: index }) => {
      const override = this.overrideHelper.deleteRowAt(index, this.props.override);
      this.props.onOverrideChange(override);

      if (index >= this.overrideHelper.getBodyRowCount(override)) {
        return { selectedRowIndex: index - 1 };
      }
    });
  }

  moveRowDown = () => {
    this.setState(({ selectedRowIndex: index }) => {
      const override = this.overrideHelper.swapRows(index, index + 1, this.props.override);
      this.props.onOverrideChange(override);
      return { selectedRowIndex: index + 1 };
    });
  }

  moveRowUp = () => {
    this.setState(({ selectedRowIndex: index }) => {
      const override = this.overrideHelper.swapRows(index, index - 1, this.props.override);
      this.props.onOverrideChange(override);
      return { selectedRowIndex: index - 1 };
    });
  }

  hasColumns() {
    if (this.props.override) {
      const { bodyRows, headerRows } = this.props.override;
      const row = bodyRows[0] || (headerRows && headerRows[0]);
      return row && row.cells.length > 0;
    } else {
      return Object.keys(this.defaults.cellContentById).length > 0;
    }
  }

  hasRows() {
    if (this.props.override) {
      const { bodyRows, headerRows } = this.props.override;
      return bodyRows.length > 0 || (headerRows && headerRows.length > 0);
    } else {
      return Object.keys(this.defaults.cellContentById).length > 0;
    }
  }

  getCommonSectionProps() {
    return {
      cellContentById: this.defaults.cellContentById,
      getCellKey: this.overrideHelper.getCellKey,
      selectable: this.state.editing,
      selectedColumnIndex: this.state.selectedColumnIndex,
      selectedRowIndex: this.state.selectedRowIndex,
      onCellBlur: this.handleCellBlur,
      onCellContentEditStart: this.stopEditing,
      onCellFocus: this.handleCellFocus,
      onCellOverrideChange: this.handleCellOverrideChange,
      onCellOverrideReset: this.handleCellOverrideReset,
      onCellSelect: this.handleCellSelect
    };
  }

  render() {
    const active = this.state.editing || this.state.focused || this.state.hovering;
    const highlight = active || this.props.highlight;
    const className = classNames('editable-component', 'table-component', {
      empty: !this.hasColumns() || !this.hasRows(),
      highlight
    });

    return (
      <div
        className={className}
        id={this.id}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {this.renderComponentHeader(active)}

        <table {...this.attributes}>
          {this.renderTableHeader()}
          {this.renderTableBody()}
        </table>
      </div>
    );
  }

  renderTableHeader() {
    if (!this.defaults.headerRows) {
      return null;
    }

    return (
      <TableHeader
        className={this.defaults.headerClass}
        defaultRows={this.defaults.headerRows}
        overrideRows={this.props.override ? this.props.override.headerRows : null}
        sanitize={this.props.sanitize}
        selected={this.state.selectedSection === 'headerRows'}
        {...this.getCommonSectionProps()}
      />
    );
  }

  renderTableBody() {
    return (
      <TableBody
        className={this.defaults.bodyClass}
        defaultRows={this.defaults.bodyRows}
        overrideRows={this.props.override ? this.props.override.bodyRows : null}
        sanitize={this.props.sanitize}
        selected={this.state.selectedSection === 'bodyRows'}
        {...this.getCommonSectionProps()}
      />
    );
  }

  renderComponentHeader(active) {
    if (!active) {
      return null;
    }

    return (
      <div className="control header" name="control-header">
        {this.state.editing ? this.renderEditControls() : this.renderNormalControls()}
      </div>
    );
  }

  renderNormalControls() {
    return (
      <NormalControls
        allowColumnEditing={this.props.allowColumnEditing}
        allowRowEditing={this.props.allowRowEditing}
        override={this.props.override}
        onEditStart={this.startEditing}
        onOverrideReset={this.props.onOverrideReset}
      />
    );
  }

  renderEditControls() {
    return (
      <EditControls
        allowColumnEditing={this.props.allowColumnEditing}
        allowRowEditing={this.props.allowRowEditing}
        columnCount={this.overrideHelper.getColumnCount(this.props.override)}
        rowCount={this.overrideHelper.getBodyRowCount(this.props.override)}
        selectedColumnIndex={this.state.selectedColumnIndex}
        selectedRowIndex={this.state.selectedRowIndex}
        selectedSection={this.state.selectedSection}
        onAddColumnAfter={this.addColumnAfter}
        onAddColumnBefore={this.addColumnBefore}
        onAddRowAfter={this.addRowAfter}
        onAddRowBefore={this.addRowBefore}
        onDeleteColumn={this.deleteColumn}
        onDeleteRow={this.deleteRow}
        onEditStop={this.stopEditing}
        onMoveColumnLeft={this.moveColumnLeft}
        onMoveColumnRight={this.moveColumnRight}
        onMoveRowDown={this.moveRowDown}
        onMoveRowUp={this.moveRowUp}
      />
    );
  }
}

export default Table;
